export interface RecentlyViewedClientRef {
  patientId: string;
  viewedAt: string;
}

export const RECENTLY_VIEWED_CLIENTS_COOKIE_PREFIX = 'unify.recently-viewed-clients.';

const MAX_ENTRIES = 4;
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

function cookieName(practitionerId: string): string {
  return `${RECENTLY_VIEWED_CLIENTS_COOKIE_PREFIX}${practitionerId}`;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const prefix = `${name}=`;
  for (const row of document.cookie.split(';')) {
    const trimmed = row.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return undefined;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function getRecentlyViewedClients(practitionerId: string): RecentlyViewedClientRef[] {
  const raw = readCookie(cookieName(practitionerId));
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter(
        (entry): entry is RecentlyViewedClientRef =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as RecentlyViewedClientRef).patientId === 'string' &&
          typeof (entry as RecentlyViewedClientRef).viewedAt === 'string',
      )
      .slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
}

export function setRecentlyViewedClients(practitionerId: string, clients: RecentlyViewedClientRef[]): void {
  const trimmed = clients.slice(0, MAX_ENTRIES);
  const name = cookieName(practitionerId);
  if (trimmed.length === 0) {
    deleteCookie(name);
    return;
  }
  writeCookie(name, JSON.stringify(trimmed));
}

export function addRecentlyViewedClient(
  practitionerId: string,
  patientId: string,
  viewedAt: string = new Date().toISOString(),
): RecentlyViewedClientRef[] {
  const rest = getRecentlyViewedClients(practitionerId).filter((entry) => entry.patientId !== patientId);
  const updated = [{ patientId, viewedAt }, ...rest].slice(0, MAX_ENTRIES);
  setRecentlyViewedClients(practitionerId, updated);
  return updated;
}
